import { notifyAiSettingsChanged } from './aiSettings';

/**
 * AI provider API keys, stored in the Vault — one hidden item per provider.
 *
 * Follows the dbVaultConnections precedent exactly: items named "__…" are
 * internal and hidden from the vault's own list UI, envelopes are read via
 * vault_list without decrypting, and the key itself is NEVER read here: the
 * adapters get a placeholder that Rust resolves (card 6.4, `getAiKey`). The item type is `custom` — an EXISTING type in both the
 * JS registry (VaultItemModal VAULT_TYPE_ORDER) and the Rust allowlist
 * (vault.rs), whose derive_index is `{}` so the key material NEVER appears
 * in the plaintext index (a team item's index goes on the wire).
 *
 * There is deliberately NO fallback to localStorage or any plain store:
 * the vault requirement is the point. A locked or absent vault REFUSES with
 * a typed error the caller can turn into an unlock prompt — silently
 * degrading to plaintext would defeat the design.
 */

/**
 * Lazily imported: `@tauri-apps/api/core` is untranspiled ESM, so a static
 * import here is inherited by every module that reaches keyStore — the engine,
 * every provider adapter, and every test touching either. It also ships the
 * native bridge to the browser build, which has no vault to talk to.
 */
async function tauriInvoke(...args) {
  const { invoke } = await import('@tauri-apps/api/core');
  return invoke(...args);
}

export class AiKeyVaultError extends Error {
  constructor(code, message) {
    super(message);
    this.name = 'AiKeyVaultError';
    /** 'vault_locked' | 'vault_unavailable' */
    this.code = code;
  }
}

const ITEM_TYPE = 'custom';
const itemName = (provider) => `__ai_key_${provider}`;

async function requireUnlockedVault() {
  let status = null;
  try {
    status = await tauriInvoke('vault_status');
  } catch (e) {
    throw new AiKeyVaultError(
      'vault_unavailable',
      'The vault is not available — AI keys are stored in your encrypted vault.'
    );
  }
  if (!status?.unlocked) {
    throw new AiKeyVaultError('vault_locked', 'Unlock your vault to use your AI key.');
  }
}

async function findEnvelope(provider) {
  const list = await tauriInvoke('vault_list');
  return (Array.isArray(list) ? list : []).find(
    (it) => it?.type === ITEM_TYPE && it?.name === itemName(provider)
  ) || null;
}

/**
 * 'unlocked' | 'locked' | 'unavailable' — never throws. 'locked' covers
 * "vault exists but is locked" AND "no vault set up yet": the passphrase
 * gate (openVaultGate) shows the right ceremony for either, so callers only
 * need one button.
 */
export async function vaultKeyState() {
  try {
    const status = await tauriInvoke('vault_status');
    return status?.unlocked ? 'unlocked' : 'locked';
  } catch {
    return 'unavailable';
  }
}

/** Whether a key is saved for `provider` (no decryption). Typed throw when the vault can't answer. */
export async function hasAiKey(provider) {
  await requireUnlockedVault();
  return !!(await findEnvelope(provider));
}

/**
 * What the provider adapters put where the key goes: a PLACEHOLDER, never the
 * key (card 6.4). Rust's transports (`send_http_request`, `ai_stream_start`)
 * swap it for the vault's key just before sending — and only for that
 * provider's own https host (`src-tauri/src/ai_key_ref.rs`). So a stored key
 * is never decrypted into the page.
 */
export const AI_KEY_PLACEHOLDER_PREFIX = 'apiboo-vault-ai-key:';

/** The key's placeholder, or null when none is saved. Typed throw when locked/absent. */
export async function getAiKey(provider) {
  await requireUnlockedVault();
  const envelope = await findEnvelope(provider);
  if (!envelope) return null;
  return `${AI_KEY_PLACEHOLDER_PREFIX}${provider}`;
}

/** Create or replace the stored key for `provider`. Typed throw when locked/absent. */
export async function setAiKey(provider, apiKey) {
  if (typeof apiKey !== 'string' || !apiKey.trim()) {
    throw new Error('API key must not be empty');
  }
  await requireUnlockedVault();
  const envelope = await findEnvelope(provider);
  await tauriInvoke('vault_put_item', {
    item: {
      // Absent id creates; the vault mints its own (dbVaultConnections
      // precedent). Reusing the existing id replaces in place.
      id: envelope?.id,
      type: ITEM_TYPE,
      name: itemName(provider),
      data: { provider, apiKey: apiKey.trim() },
    },
  });
  notifyAiSettingsChanged();
}

/** Delete the stored key for `provider` (no-op when none). Typed throw when locked/absent. */
export async function removeAiKey(provider) {
  await requireUnlockedVault();
  const envelope = await findEnvelope(provider);
  if (envelope) {
    await tauriInvoke('vault_delete_item', { id: envelope.id });
    notifyAiSettingsChanged();
  }
}
